import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import './BorrifacaoTab.css';
import { BotaoDownloadBorrifacaoPDF, FormularioBorrifacaoPDFDocument } from './FormularioBorrifacaoPDF';
import { pdf } from '@react-pdf/renderer';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

/* ─────────────────────────────────────────────
   BORRIFAÇÃO — Resumo Semanal
───────────────────────────────────────────── */

const LINHA_VAZIA = {
    data: '', localidade: '', concluida: '',
    ct_pos: '', ct_neg: '', ct_total: '',
    cp_fech: '', cp_rec: '', cp_total: '',
    at_pos: '', at_neg: '', at_total: '',
    ud_pos: '', ud_neg: '', ud_total: '',
    hab_proteg: '', consumo_inset: '', n_triat: '', hd: ''
};

const BorrifacaoTab = ({ municipioId, municipioNome = '' }) => {
    const [cabecalho, setCabecalho] = useState({
        dist_sanit: '', sub_distrito: '', turma: '',
        guarda_chefe: '', municipio: municipioNome, fase_trabalho: '', semana: ''
    });
    const [linhas, setLinhas] = useState(() =>
        Array.from({ length: 10 }, () => ({ ...LINHA_VAZIA, _id: Math.random() }))
    );
    const [rodape, setRodape] = useState({
        rendimento: '', saldo_bhc: '', ass_guarda: '', ass_inspetor: '', data_ass: ''
    });

    const [salvando, setSalvando] = useState(false);
    const [resumos, setResumos] = useState([]);
    const [loadingResumos, setLoadingResumos] = useState(true);
    const [editandoId, setEditandoId] = useState(null);
    const [busca, setBusca] = useState('');
    const formRef = useRef(null);

    // Seleção múltipla
    const [modoSelecao, setModoSelecao] = useState(false);
    const [selecionados, setSelecionados] = useState(new Set());
    const [gerandoZip, setGerandoZip] = useState(false);

    const setCab = (f, v) => setCabecalho(p => ({ ...p, [f]: v }));
    const setRod = (f, v) => setRodape(p => ({ ...p, [f]: v }));

    /* ── Busca resumos salvos ── */
    const fetchResumos = useCallback(async () => {
        if (!municipioId) return;
        setLoadingResumos(true);
        try {
            const { data, error } = await supabase
                .from('resumos_borrifacao')
                .select('*, resumos_borrifacao_linhas(*)')
                .eq('municipio_id', municipioId)
                .order('created_at', { ascending: false });
            if (error) throw error;
            setResumos(data || []);
        } catch (err) {
            console.error('Erro ao buscar resumos:', err);
        } finally {
            setLoadingResumos(false);
        }
    }, [municipioId]);

    useEffect(() => { fetchResumos(); }, [fetchResumos]);

    useEffect(() => {
        if (municipioNome) {
            setCabecalho(prev => ({ ...prev, municipio: prev.municipio || municipioNome }));
        }
    }, [municipioNome]);

    /* ── Carrega um resumo para edição ── */
    const handleCarregarParaEdicao = (r) => {
        setEditandoId(r.id);
        setCabecalho({
            dist_sanit:    r.dist_sanit    || '',
            sub_distrito:  r.sub_distrito  || '',
            turma:         r.turma         || '',
            guarda_chefe:  r.guarda_chefe  || '',
            municipio:     r.municipio_nome|| '',
            fase_trabalho: r.fase_trabalho || '',
            semana:        r.semana        || '',
        });
        const linhasCarregadas = (r.resumos_borrifacao_linhas || [])
            .sort((a, b) => a.ordem - b.ordem)
            .map(l => ({
                _id:           Math.random(),
                data:          l.data          || '',
                localidade:    l.localidade    || '',
                concluida:     l.concluida     || '',
                ct_pos:        l.ct_pos  != null ? String(l.ct_pos)        : '',
                ct_neg:        l.ct_neg  != null ? String(l.ct_neg)        : '',
                ct_total:      l.ct_total!= null ? String(l.ct_total)      : '',
                cp_fech:       l.cp_fech != null ? String(l.cp_fech)       : '',
                cp_rec:        l.cp_rec  != null ? String(l.cp_rec)        : '',
                cp_total:      l.cp_total!= null ? String(l.cp_total)      : '',
                at_pos:        l.at_pos  != null ? String(l.at_pos)        : '',
                at_neg:        l.at_neg  != null ? String(l.at_neg)        : '',
                at_total:      l.at_total!= null ? String(l.at_total)      : '',
                ud_pos:        l.ud_pos  != null ? String(l.ud_pos)        : '',
                ud_neg:        l.ud_neg  != null ? String(l.ud_neg)        : '',
                ud_total:      l.ud_total!= null ? String(l.ud_total)      : '',
                hab_proteg:    l.hab_proteg    != null ? String(l.hab_proteg)    : '',
                consumo_inset: l.consumo_inset != null ? String(l.consumo_inset) : '',
                n_triat:       l.n_triat       != null ? String(l.n_triat)       : '',
                hd:            l.hd            || '',
            }));
        setLinhas(
            linhasCarregadas.length > 0
                ? linhasCarregadas
                : Array.from({ length: 10 }, () => ({ ...LINHA_VAZIA, _id: Math.random() }))
        );
        setRodape({
            rendimento:   r.rendimento   || '',
            saldo_bhc:    r.saldo_bhc    || '',
            ass_guarda:   r.ass_guarda   || '',
            ass_inspetor: r.ass_inspetor || '',
            data_ass:     r.data_ass     || '',
        });
        setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
    };

    const resetForm = () => {
        setEditandoId(null);
        setCabecalho({ dist_sanit: '', sub_distrito: '', turma: '', guarda_chefe: '', municipio: municipioNome, fase_trabalho: '', semana: '' });
        setLinhas(Array.from({ length: 10 }, () => ({ ...LINHA_VAZIA, _id: Math.random() })));
        setRodape({ rendimento: '', saldo_bhc: '', ass_guarda: '', ass_inspetor: '', data_ass: '' });
    };

    /* ── Exclui resumo ── */
    const handleExcluir = async (id) => {
        if (!window.confirm('Excluir este resumo semanal? Esta ação não pode ser desfeita.')) return;
        try {
            const { error } = await supabase.from('resumos_borrifacao').delete().eq('id', id);
            if (error) throw error;
            if (editandoId === id) resetForm();
            fetchResumos();
        } catch (err) {
            console.error(err);
            alert('Erro ao excluir resumo.');
        }
    };

    /* ── Salva / atualiza resumo ── */
    const handleSalvar = async () => {
        if (!municipioId) { alert('Município não identificado.'); return; }
        if (!cabecalho.semana) { alert('Informe a semana antes de salvar.'); return; }
        setSalvando(true);
        try {
            const payload = {
                municipio_id:   municipioId,
                dist_sanit:     cabecalho.dist_sanit     || null,
                sub_distrito:   cabecalho.sub_distrito   || null,
                turma:          cabecalho.turma           || null,
                guarda_chefe:   cabecalho.guarda_chefe   || null,
                municipio_nome: cabecalho.municipio      || null,
                fase_trabalho:  cabecalho.fase_trabalho  || null,
                semana:         cabecalho.semana         || null,
                rendimento:     rodape.rendimento        || null,
                saldo_bhc:      rodape.saldo_bhc         || null,
                ass_guarda:     rodape.ass_guarda        || null,
                ass_inspetor:   rodape.ass_inspetor      || null,
                data_ass:       rodape.data_ass          || null,
            };

            let resumoId;
            if (editandoId) {
                const { error: errUp } = await supabase.from('resumos_borrifacao').update(payload).eq('id', editandoId);
                if (errUp) throw errUp;
                await supabase.from('resumos_borrifacao_linhas').delete().eq('resumo_id', editandoId);
                resumoId = editandoId;
            } else {
                const { data: resumo, error: errResumo } = await supabase
                    .from('resumos_borrifacao').insert(payload).select().single();
                if (errResumo) throw errResumo;
                resumoId = resumo.id;
            }

            const linhasComTotais = linhas.map(l => ({
                ...l,
                ct_total: (Number(l.ct_pos)  || 0) + (Number(l.ct_neg)  || 0) || 0,
                cp_total: (Number(l.cp_fech) || 0) + (Number(l.cp_rec)  || 0) || 0,
                at_total: (Number(l.at_pos)  || 0) + (Number(l.at_neg)  || 0) || 0,
                ud_total: (Number(l.ud_pos)  || 0) + (Number(l.ud_neg)  || 0) || 0,
            }));
            const linhasValidas = linhasComTotais.filter(l => l.localidade || l.data);
            if (linhasValidas.length > 0) {
                const linhasPayload = linhasValidas.map((l, idx) => ({
                    resumo_id:     resumoId,
                    ordem:         idx,
                    data:          l.data          || null,
                    localidade:    l.localidade    || null,
                    concluida:     l.concluida     || null,
                    ct_pos:        Number(l.ct_pos)        || null,
                    ct_neg:        Number(l.ct_neg)        || null,
                    ct_total:      Number(l.ct_total)      || null,
                    cp_fech:       Number(l.cp_fech)       || null,
                    cp_rec:        Number(l.cp_rec)        || null,
                    cp_total:      Number(l.cp_total)      || null,
                    at_pos:        Number(l.at_pos)        || null,
                    at_neg:        Number(l.at_neg)        || null,
                    at_total:      Number(l.at_total)      || null,
                    ud_pos:        Number(l.ud_pos)        || null,
                    ud_neg:        Number(l.ud_neg)        || null,
                    ud_total:      Number(l.ud_total)      || null,
                    hab_proteg:    Number(l.hab_proteg)    || null,
                    consumo_inset: Number(l.consumo_inset) || null,
                    n_triat:       Number(l.n_triat)       || null,
                    hd:            l.hd || null,
                }));
                const { error: errLinhas } = await supabase.from('resumos_borrifacao_linhas').insert(linhasPayload);
                if (errLinhas) throw errLinhas;
            }

            alert(editandoId ? 'Resumo atualizado com sucesso!' : 'Resumo salvo com sucesso!');
            resetForm();
            fetchResumos();
        } catch (err) {
            console.error(err);
            alert('Erro ao salvar resumo.');
        } finally {
            setSalvando(false);
        }
    };

    const setLinha = (idx, field, value) =>
        setLinhas(prev => prev.map((l, i) => i === idx ? { ...l, [field]: value } : l));

    const addLinha    = () => setLinhas(prev => [...prev, { ...LINHA_VAZIA, _id: Math.random() }]);
    const removeLinha = (idx) => setLinhas(prev => prev.filter((_, i) => i !== idx));

    /* ── Auto-totais por linha ── */
    const withTotals = linhas.map(l => ({
        ...l,
        ct_total: (Number(l.ct_pos)  || 0) + (Number(l.ct_neg)  || 0) || '',
        cp_total: (Number(l.cp_fech) || 0) + (Number(l.cp_rec)  || 0) || '',
        at_total: (Number(l.at_pos)  || 0) + (Number(l.at_neg)  || 0) || '',
        ud_total: (Number(l.ud_pos)  || 0) + (Number(l.ud_neg)  || 0) || '',
    }));

    /* ── Linha TOTAL ── */
    const totalRow = withTotals.reduce((acc, l) => ({
        ct_pos:        (acc.ct_pos        || 0) + (Number(l.ct_pos)        || 0),
        ct_neg:        (acc.ct_neg        || 0) + (Number(l.ct_neg)        || 0),
        ct_total:      (acc.ct_total      || 0) + (Number(l.ct_total)      || 0),
        cp_fech:       (acc.cp_fech       || 0) + (Number(l.cp_fech)       || 0),
        cp_rec:        (acc.cp_rec        || 0) + (Number(l.cp_rec)        || 0),
        cp_total:      (acc.cp_total      || 0) + (Number(l.cp_total)      || 0),
        at_pos:        (acc.at_pos        || 0) + (Number(l.at_pos)        || 0),
        at_neg:        (acc.at_neg        || 0) + (Number(l.at_neg)        || 0),
        at_total:      (acc.at_total      || 0) + (Number(l.at_total)      || 0),
        ud_pos:        (acc.ud_pos        || 0) + (Number(l.ud_pos)        || 0),
        ud_neg:        (acc.ud_neg        || 0) + (Number(l.ud_neg)        || 0),
        ud_total:      (acc.ud_total      || 0) + (Number(l.ud_total)      || 0),
        hab_proteg:    (acc.hab_proteg    || 0) + (Number(l.hab_proteg)    || 0),
        consumo_inset: (acc.consumo_inset || 0) + (Number(l.consumo_inset) || 0),
        n_triat:       (acc.n_triat       || 0) + (Number(l.n_triat)       || 0),
    }), {});

    /* ── Filtragem ── */
    const resumosFiltrados = useMemo(() => {
        if (!busca.trim()) return resumos;
        const q = busca.toLowerCase();
        return resumos.filter(r =>
            (r.semana        || '').toLowerCase().includes(q) ||
            (r.guarda_chefe  || '').toLowerCase().includes(q) ||
            (r.fase_trabalho || '').toLowerCase().includes(q) ||
            (r.municipio_nome|| '').toLowerCase().includes(q)
        );
    }, [resumos, busca]);

    /* ── Helper: input na tabela ── */
    const numCell = (l, field, idx, isTotal = false) => (
        <td key={field} className={isTotal ? 'borr-data-td-total' : 'borr-data-td'}>
            <input
                type="text"
                className="borr-data-input borr-data-input-num"
                value={l[field] || ''}
                readOnly={isTotal}
                autoComplete="off"
                onChange={e => !isTotal && setLinha(idx, field, e.target.value)}
            />
        </td>
    );

    /* ── ZIP de PDFs ── */
    const handleBaixarZip = async () => {
        if (selecionados.size === 0) return;
        setGerandoZip(true);
        const zip = new JSZip();
        
        try {
            const itensParaProcessar = resumosFiltrados.filter(r => selecionados.has(r.id));
            
            for (let i = 0; i < itensParaProcessar.length; i++) {
                const r = itensParaProcessar[i];
                
                // Mapeia os dados no formato esperado pelo PDF
                const cab = {
                    dist_sanit:    r.dist_sanit || '',
                    sub_distrito:  r.sub_distrito || '',
                    turma:         r.turma || '',
                    guarda_chefe:  r.guarda_chefe || '',
                    municipio:     r.municipio_nome || '',
                    fase_trabalho: r.fase_trabalho || '',
                    semana:        r.semana || '',
                };
                
                const lines = (r.resumos_borrifacao_linhas || [])
                    .sort((a, b) => a.ordem - b.ordem)
                    .map(l => ({
                        data:          l.data || '',
                        localidade:    l.localidade || '',
                        concluida:     l.concluida || '',
                        ct_pos:        l.ct_pos, ct_neg: l.ct_neg, ct_total: l.ct_total,
                        cp_fech:       l.cp_fech, cp_rec: l.cp_rec, cp_total: l.cp_total,
                        at_pos:        l.at_pos, at_neg: l.at_neg, at_total: l.at_total,
                        ud_pos:        l.ud_pos, ud_neg: l.ud_neg, ud_total: l.ud_total,
                        hab_proteg:    l.hab_proteg, consumo_inset: l.consumo_inset,
                        n_triat:       l.n_triat, hd: l.hd || ''
                    }));

                const rod = {
                    rendimento:   r.rendimento || '',
                    saldo_bhc:    r.saldo_bhc || '',
                    ass_guarda:   r.ass_guarda || '',
                    ass_inspetor: r.ass_inspetor || '',
                    data_ass:     r.data_ass || '',
                };

                const tRow = lines.reduce((acc, l) => ({
                    ct_pos: (acc.ct_pos || 0) + (Number(l.ct_pos) || 0),
                    ct_neg: (acc.ct_neg || 0) + (Number(l.ct_neg) || 0),
                    ct_total: (acc.ct_total || 0) + (Number(l.ct_total) || 0),
                    cp_fech: (acc.cp_fech || 0) + (Number(l.cp_fech) || 0),
                    cp_rec: (acc.cp_rec || 0) + (Number(l.cp_rec) || 0),
                    cp_total: (acc.cp_total || 0) + (Number(l.cp_total) || 0),
                    at_pos: (acc.at_pos || 0) + (Number(l.at_pos) || 0),
                    at_neg: (acc.at_neg || 0) + (Number(l.at_neg) || 0),
                    at_total: (acc.at_total || 0) + (Number(l.at_total) || 0),
                    ud_pos: (acc.ud_pos || 0) + (Number(l.ud_pos) || 0),
                    ud_neg: (acc.ud_neg || 0) + (Number(l.ud_neg) || 0),
                    ud_total: (acc.ud_total || 0) + (Number(l.ud_total) || 0),
                    hab_proteg: (acc.hab_proteg || 0) + (Number(l.hab_proteg) || 0),
                    consumo_inset: (acc.consumo_inset || 0) + (Number(l.consumo_inset) || 0),
                    n_triat: (acc.n_triat || 0) + (Number(l.n_triat) || 0),
                }), {});

                const doc = (
                    <FormularioBorrifacaoPDFDocument 
                        data={lines}
                        cabecalho={cab}
                        rodape={rod}
                        totalRow={tRow}
                    />
                );

                const blob = await pdf(doc).toBlob();
                const nomeF = `Resumo_Borrifacao_Semana_${r.semana || i}.pdf`.replace(/\//g, '-');
                zip.file(nomeF, blob);
            }

            const zipBlob = await zip.generateAsync({ type: 'blob' });
            saveAs(zipBlob, `Resumos_Borrifacao_${new Date().toLocaleDateString('pt-BR').replace(/\//g, '-')}.zip`);
        } catch (err) {
            console.error("Erro ao gerar ZIP:", err);
            alert("Erro ao gerar o arquivo ZIP.");
        } finally {
            setGerandoZip(false);
        }
    };

    const toggleSelecionado = (id) => {
        setSelecionados(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const toggleTodos = () => {
        if (selecionados.size === resumosFiltrados.length) {
            setSelecionados(new Set());
        } else {
            setSelecionados(new Set(resumosFiltrados.map(r => r.id)));
        }
    };

    return (
        <div className="borr-container" ref={formRef}>

            {/* ══════════════════════════════════════════
                FORMULÁRIO ÚNICO
            ══════════════════════════════════════════ */}
            <div className="borr-form">

                {/* Cabeçalho do card */}
                <div className="borr-form-header">
                    <span className="borr-form-header-title">
                        {editandoId ? 'Editando Resumo Semanal' : 'Novo Resumo Semanal'}
                    </span>
                    {editandoId && (
                        <div className="borr-edit-badge">
                            ✏️ Modo edição
                            <button className="borr-edit-cancel" onClick={resetForm}>
                                Cancelar
                            </button>
                        </div>
                    )}
                </div>

                <div className="borr-form-body">

                    {/* ── Seção 1: Identificação ── */}
                    <div className="borr-field-grid-4">
                        {[
                            ['Dist. Sanitário',  'dist_sanit',    'text'],
                            ['Sub-Distrito',     'sub_distrito',  'text'],
                            ['Fase de Trabalho', 'fase_trabalho', 'text'],
                        ].map(([lbl, field, type]) => (
                            <div className="borr-field" key={field}>
                                <label className="borr-label">{lbl}</label>
                                <input
                                    type={type}
                                    className="borr-input"
                                    value={cabecalho[field]}
                                    autoComplete="off"
                                    onChange={e => setCab(field, e.target.value)}
                                    placeholder={lbl}
                                />
                            </div>
                        ))}
                        <div className="borr-field">
                            <label className="borr-label">Município</label>
                            <input
                                type="text"
                                className="borr-input"
                                value={cabecalho.municipio}
                                readOnly
                                style={{ background: '#f9fafb', color: '#6b7280', cursor: 'default' }}
                            />
                        </div>
                    </div>

                    <div className="borr-field-grid-3">
                        {[
                            ['Semana',            'semana',       'text'],
                            ['Turma',             'turma',        'text'],
                            ['Nome Guarda Chefe', 'guarda_chefe', 'text'],
                        ].map(([lbl, field, type]) => (
                            <div className="borr-field" key={field}>
                                <label className="borr-label">{lbl}</label>
                                <input
                                    type={type}
                                    className="borr-input"
                                    value={cabecalho[field]}
                                    autoComplete="off"
                                    onChange={e => setCab(field, e.target.value)}
                                    placeholder={lbl}
                                />
                            </div>
                        ))}
                    </div>

                    {/* ── Divisor ── */}
                    <div className="borr-section-divider">
                        <span>Registro por Localidade</span>
                    </div>

                    {/* ── Tabela de linhas ── */}
                    <div className="borr-data-scroll">
                        <table className="borr-data-table">
                            <thead>
                                {/* Linha 1 — grupos */}
                                <tr className="borr-thead-groups">
                                    <th rowSpan={2} style={{ width: 108, textAlign: 'left', paddingLeft: 12 }}>Data</th>
                                    <th rowSpan={2} style={{ minWidth: 130, textAlign: 'left', paddingLeft: 8 }}>Localidade</th>
                                    <th rowSpan={2} style={{ width: 70 }}>Concluída</th>
                                    <th colSpan={3} className="borr-th-ct">Casas Trabalhadas</th>
                                    <th colSpan={3} className="borr-th-cp">Casas Pendentes</th>
                                    <th colSpan={3} className="borr-th-at">Anexos Trabalhados</th>
                                    <th colSpan={3} className="borr-th-ud">Unidades Domiciliares</th>
                                    <th rowSpan={2} style={{ width: 62 }}>Hab. Protegidos</th>
                                    <th rowSpan={2} style={{ width: 62 }}>Inseticida (cps)</th>
                                    <th rowSpan={2} style={{ width: 56 }}>Nº Triat.</th>
                                    <th rowSpan={2} style={{ width: 44 }}>HD</th>
                                    <th rowSpan={2} style={{ width: 34 }}></th>
                                </tr>
                                {/* Linha 2 — sub-cabeçalhos */}
                                <tr className="borr-thead-sub">
                                    <th>Positivas</th>
                                    <th>Negativas</th>
                                    <th className="borr-sub-total">Total</th>
                                    <th>Fechadas</th>
                                    <th>Recusas</th>
                                    <th className="borr-sub-total">Total</th>
                                    <th>Positivos</th>
                                    <th>Negativos</th>
                                    <th className="borr-sub-total">Total</th>
                                    <th>Positivas</th>
                                    <th>Negativas</th>
                                    <th className="borr-sub-total">Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {withTotals.map((l, idx) => (
                                    <tr key={l._id}>
                                        <td className="borr-data-td">
                                            <input
                                                type="date"
                                                className="borr-data-input borr-data-input-date"
                                                value={l.data}
                                                onChange={e => setLinha(idx, 'data', e.target.value)}
                                            />
                                        </td>
                                        <td className="borr-data-td">
                                            <input
                                                type="text"
                                                className="borr-data-input"
                                                style={{ minWidth: 120 }}
                                                value={l.localidade}
                                                autoComplete="off"
                                                onChange={e => setLinha(idx, 'localidade', e.target.value)}
                                            />
                                        </td>
                                        <td className="borr-data-td" style={{ textAlign: 'center' }}>
                                            <input
                                                type="text"
                                                className="borr-data-input borr-data-input-num"
                                                value={l.concluida}
                                                autoComplete="off"
                                                onChange={e => setLinha(idx, 'concluida', e.target.value)}
                                            />
                                        </td>
                                        {numCell(l, 'ct_pos',   idx)}
                                        {numCell(l, 'ct_neg',   idx)}
                                        {numCell(l, 'ct_total', idx, true)}
                                        {numCell(l, 'cp_fech',  idx)}
                                        {numCell(l, 'cp_rec',   idx)}
                                        {numCell(l, 'cp_total', idx, true)}
                                        {numCell(l, 'at_pos',   idx)}
                                        {numCell(l, 'at_neg',   idx)}
                                        {numCell(l, 'at_total', idx, true)}
                                        {numCell(l, 'ud_pos',   idx)}
                                        {numCell(l, 'ud_neg',   idx)}
                                        {numCell(l, 'ud_total', idx, true)}
                                        {numCell(l, 'hab_proteg',    idx)}
                                        {numCell(l, 'consumo_inset', idx)}
                                        {numCell(l, 'n_triat',       idx)}
                                        <td className="borr-data-td">
                                            <input
                                                type="text"
                                                className="borr-data-input borr-data-input-num"
                                                value={l.hd}
                                                autoComplete="off"
                                                onChange={e => setLinha(idx, 'hd', e.target.value)}
                                            />
                                        </td>
                                        <td className="borr-data-td" style={{ textAlign: 'center' }}>
                                            <button
                                                className="borr-row-remove"
                                                onClick={() => removeLinha(idx)}
                                                title="Remover linha"
                                            >×</button>
                                        </td>
                                    </tr>
                                ))}

                                {/* Linha TOTAL */}
                                <tr className="borr-data-tr-total">
                                    <td colSpan={3} style={{ textAlign: 'center', fontWeight: 700 }}>TOTAL GERAL</td>
                                    {['ct_pos','ct_neg','ct_total','cp_fech','cp_rec','cp_total',
                                      'at_pos','at_neg','at_total','ud_pos','ud_neg','ud_total',
                                      'hab_proteg','consumo_inset','n_triat'].map(f => (
                                        <td key={f}>{totalRow[f] || '—'}</td>
                                      ))}
                                    <td></td>
                                    <td></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <button className="borr-add-row-btn" onClick={addLinha}>
                        + Adicionar linha
                    </button>

                    {/* ── Divisor ── */}
                    <div className="borr-section-divider">
                        <span>Rodapé do Relatório</span>
                    </div>

                    {/* ── Rodapé ── */}
                    <div className="borr-field-grid-rod">
                        {[
                            ['Rendimento / Homem / Dia', 'rendimento',   'text'],
                            ['Saldo de BHC ou K-O',       'saldo_bhc',    'text'],
                            ['Data de Assinatura',         'data_ass',     'date'],
                            ['Ass. Guarda Chefe',          'ass_guarda',   'text'],
                            ['Visto Inspetor',             'ass_inspetor', 'text'],
                        ].map(([lbl, field, type]) => (
                            <div className="borr-field" key={field}>
                                <label className="borr-label">{lbl}</label>
                                <input
                                    type={type}
                                    className="borr-input"
                                    value={rodape[field]}
                                    autoComplete="off"
                                    onChange={e => setRod(field, e.target.value)}
                                    placeholder={lbl}
                                />
                            </div>
                        ))}
                    </div>

                    {/* ── Barra de salvar — dentro do form, na base ── */}
                    <div className="borr-action-bar">
                        <button
                            className="borr-btn-save"
                            onClick={handleSalvar}
                            disabled={salvando}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                                <polyline points="17 21 17 13 7 13 7 21"/>
                                <polyline points="7 3 7 8 15 8"/>
                            </svg>
                            {salvando ? 'Salvando...' : editandoId ? 'Salvar Alterações' : 'Salvar Resumo Semanal'}
                        </button>

                        {editandoId && (
                            <BotaoDownloadBorrifacaoPDF
                                data={linhas}
                                cabecalho={cabecalho}
                                rodape={rodape}
                                totalRow={totalRow}
                            />
                        )}
                    </div>

                </div>{/* /borr-form-body */}
            </div>{/* /borr-form */}


            {/* ══════════════════════════════════════════
                TABELA DE RESUMOS CADASTRADOS
            ══════════════════════════════════════════ */}
            <div className="borr-list-card">
                <div className="borr-table-toolbar">
                    <h3 className="borr-table-title">Resumos Semanais Cadastrados</h3>

                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                        <button
                            className="btn-download-trigger"
                            style={{
                                background: modoSelecao ? '#1d4ed8' : '#2563eb',
                                height: '36px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '0 16px',
                                borderRadius: '6px',
                                color: 'white',
                                fontWeight: '600',
                                border: 'none',
                                cursor: 'pointer'
                            }}
                            onClick={() => {
                                setModoSelecao(!modoSelecao);
                                setSelecionados(new Set());
                            }}
                        >
                             <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                <polyline points="7 10 12 15 17 10"/>
                                <line x1="12" y1="15" x2="12" y2="3"/>
                            </svg>
                            {modoSelecao ? 'Cancelar' : 'Baixar PDFs'}
                        </button>

                        {modoSelecao && selecionados.size > 0 && (
                            <button
                                className="btn-execute-download"
                                style={{
                                    background: gerandoZip ? '#94a3b8' : '#059669',
                                    height: '36px', padding: '0 16px', borderRadius: '6px', color: 'white', fontWeight: '600', border: 'none', cursor: gerandoZip ? 'wait' : 'pointer'
                                }}
                                onClick={handleBaixarZip}
                                disabled={gerandoZip}
                            >
                                {gerandoZip ? 'Gerando...' : `Baixar ${selecionados.size} Resumo(s)`}
                            </button>
                        )}

                        <div className="borr-search-wrapper">
                            <svg className="borr-search-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11ZM2 9a7 7 0 1 1 12.452 4.391l3.328 3.329a.75.75 0 1 1-1.06 1.06l-3.329-3.328A7 7 0 0 1 2 9Z" clipRule="evenodd" />
                            </svg>
                            <input
                                className="borr-search-input"
                                type="text"
                                placeholder="Buscar..."
                                value={busca}
                                onChange={e => setBusca(e.target.value)}
                            />
                        </div>
                    </div>
                </div>

                <div className="borr-table-wrapper">
                    <table className="borr-table">
                        <thead>
                            <tr>
                                {modoSelecao && (
                                    <th className="borr-td-center" style={{ width: 40 }}>
                                        <input type="checkbox" checked={selecionados.size === resumosFiltrados.length && resumosFiltrados.length > 0} onChange={toggleTodos} />
                                    </th>
                                )}
                                <th className="borr-td-center" style={{ width: 120 }}>Data de Inclusão</th>
                                <th className="borr-td-left">Semana</th>
                                <th className="borr-td-left">Guarda Chefe</th>
                                <th className="borr-td-left">Fase</th>
                                <th className="borr-td-center">Casas Trabalhadas</th>
                                <th className="borr-td-center">Casas Pendentes</th>
                                <th className="borr-td-center">Anexos Trabalhados</th>
                                <th className="borr-td-center">Unidades Domiciliares</th>
                                <th className="borr-td-center" style={{ width: 160 }}>Ação</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loadingResumos ? (
                                <tr><td colSpan={modoSelecao ? 10 : 9} className="borr-empty">Carregando...</td></tr>
                            ) : resumosFiltrados.length === 0 ? (
                                <tr>
                                    <td colSpan={modoSelecao ? 10 : 9} className="borr-empty">
                                        {busca
                                            ? 'Nenhum resumo encontrado para a busca.'
                                            : 'Nenhum resumo cadastrado ainda.'}
                                    </td>
                                </tr>
                            ) : (
                                resumosFiltrados.map(r => {
                                    const linhasR = r.resumos_borrifacao_linhas || [];
                                    const soma = f => linhasR.reduce((s, l) => s + (Number(l[f]) || 0), 0);
                                    const dataInclusao = r.created_at
                                        ? new Date(r.created_at).toLocaleDateString('pt-BR')
                                        : '—';
                                    return (
                                        <tr key={r.id} onClick={() => modoSelecao && toggleSelecionado(r.id)} style={{ cursor: modoSelecao ? 'pointer' : 'default' }}>
                                            {modoSelecao && (
                                                <td className="borr-td-center">
                                                    <input type="checkbox" checked={selecionados.has(r.id)} onChange={() => {}} />
                                                </td>
                                            )}
                                            <td className="borr-td-center" style={{ color: '#64748b', fontSize: '12px' }}>{dataInclusao}</td>
                                            <td className="borr-td-left" style={{ fontWeight: 600, color: '#1e293b' }}>{r.semana || '—'}</td>
                                            <td className="borr-td-left" style={{ color: '#475569' }}>{r.guarda_chefe || '—'}</td>
                                            <td className="borr-td-left" style={{ color: '#475569' }}>{r.fase_trabalho || '—'}</td>
                                            <td className="borr-td-center">
                                                <span className="borr-stat" style={{ color: '#2563eb' }}>{soma('ct_pos') + soma('ct_neg')}</span>
                                            </td>
                                            <td className="borr-td-center">
                                                <span className="borr-stat" style={{ color: '#d97706' }}>{soma('cp_fech') + soma('cp_rec')}</span>
                                            </td>
                                            <td className="borr-td-center">
                                                <span className="borr-stat" style={{ color: '#7c3aed' }}>{soma('at_pos') + soma('at_neg')}</span>
                                            </td>
                                            <td className="borr-td-center">
                                                <span className="borr-stat" style={{ color: '#059669' }}>{soma('ud_pos') + soma('ud_neg')}</span>
                                            </td>
                                            <td className="borr-td-center">
                                                <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                                                    <button className="borr-btn-edit" onClick={(e) => { e.stopPropagation(); handleCarregarParaEdicao(r); }}>
                                                        Visualizar / Editar
                                                    </button>
                                                    <button className="borr-btn-delete" onClick={(e) => { e.stopPropagation(); handleExcluir(r.id); }} title="Excluir">
                                                        🗑️
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

        </div>
    );
};

export default BorrifacaoTab;
